import React, { useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

const SessionExpiryChecker: React.FC = () => {
  const { user, logout } = useAuth();
  const lastExpiryCheck = useRef(0);
  const intervalRef = useRef<NodeJS.Timeout>(null);

  const handleLogout = useCallback(() => {
    console.log('❌ Logging out due to session expiry...');
    logout();
  }, [logout]);

  const checkSessionExpiry = useCallback(() => {
    if (!user?.token) {
      return;
    }

    try {
      const tokenParts = user.token.split('.');
      if (tokenParts.length !== 3) {
        console.error('Invalid token format');
        handleLogout();
        return;
      }

      const payload = JSON.parse(atob(tokenParts[1]));
      const expiryTime = payload.exp * 1000;
      const now = Date.now();
      const timeLeft = (expiryTime - now) / 1000;

      if (Math.abs(timeLeft - lastExpiryCheck.current) > 30) {
        console.log(`⏰ Session expires in ${Math.round(timeLeft / 60)} minutes`);
        lastExpiryCheck.current = timeLeft;
      }

      if (timeLeft <= 10) {
        console.log('⏰ Session expired, logging out...');
        handleLogout();
        return;
      }
    } catch (error) {
      console.error('Error checking token expiry:', error);
      handleLogout();
    }
  }, [user?.token, handleLogout]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (!user?.token) {
      return;
    }

    checkSessionExpiry();

    intervalRef.current = setInterval(checkSessionExpiry, 120_000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [user?.token, checkSessionExpiry]);

  return null;
};

export default SessionExpiryChecker;
